/**
 * Multi-Firebase Service
 * 
 * Provides unified access to multiple Firebase Named Databases within the same project
 * Uses Firebase Named Databases feature (supported since SDK v9.19.0+)
 */

import { getApp } from 'firebase/app';
import { collection, getDocs, limit, query } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { db, cbtkdeducaDb, cbtkdeventosDb, notificationsDb, auth } from '../utils/init-firebase';
import { FirebaseRouter } from '../utils/firebase-collections';

class MultiFirebaseService {
  
  constructor() {
    this.connections = {};
    this.initialized = false;
  }

  /**
   * Reset the service state (for development/testing)
   */
  reset() {
    this.connections = {};
    this.initialized = false;
    // console.log('[MultiFirebaseService] Service state reset');
  }
  
  /**
   * Initialize Firebase Named Databases connections
   * Only initializes once to avoid duplicate references
   */
  initialize() {
    if (this.initialized) {
      // console.log('[MultiFirebaseService] Already initialized, skipping...');
      return;
    }
    
    try {
      // Get the single Firebase app instance
      const app = getApp(); // Gets the default app
      
      // Database padrão (SGC)
      const primaryStorage = getStorage(app);
      
      this.connections.primary = {
        app: app,
        db: db, // Default database (already initialized in init-firebase.js)
        storage: primaryStorage,
        auth: auth,
        name: 'Primary (SGC - Default Database)'
      };
      
      // Database do CBTKDeduca (Named Database no mesmo projeto)
      // Não precisa de app separado - usa o mesmo app com database diferente!
      this.connections.secondary = {
        app: app, // Mesmo app!
        db: cbtkdeducaDb, // Named database (already initialized in init-firebase.js)
        storage: primaryStorage, // Storage é compartilhado
        auth: auth, // Auth é compartilhado automaticamente!
        name: 'Secondary (CBTKDeduca - Named Database)'
      };

      // 🥋 Database do Poomsae (NOVO!)
      this.connections.tertiary = {
        app: app,
        db: cbtkdeventosDb,
        storage: primaryStorage,
        auth: auth,
        name: 'Tertiary (Poomsae - Named Database cbtkdeventos)'
      };      

      // 🔔 Database de Notificações (Named Database)
      this.connections.notifications = {
        app: app,
        db: notificationsDb,
        storage: primaryStorage,
        auth: auth,
        name: 'Notifications (Push Notifications - Named Database notifications)'
      };

      this.initialized = true;
      
      // console.log('[MultiFirebaseService] Initialized successfully with Named Databases:');
      // console.log('- Primary Database: (default) - SGC');
      // console.log('- Secondary Database: cbtkdeduca - CBTKDeduca');
      // console.log('- Tertiary Database: cbtkdeventos - Poomsae');
      // console.log('- Notifications Database: notifications - Push Notifications');
      // console.log('- Project:', app.options.projectId);
      // console.log('- Shared Authentication: YES ✅');
      
    } catch (error) {
      console.error('[MultiFirebaseService] Initialization failed:', error);
      throw error;
    }
  }
  
  /**
   * Get the appropriate Firebase connection for a collection
   * @param {string} collectionName - Name of the collection
   * @returns {Object} Firebase connection object
   */
  getConnection(collectionName) {
    if (!this.initialized) {
      this.initialize();
    }
    
    const connectionType = FirebaseRouter.getConnectionType(collectionName);
    const connection = this.connections[connectionType];
    
    if (!connection) {
      throw new Error(`Firebase connection '${connectionType}' not found`);
    }
    
    // Log for debugging in development
    if (import.meta.env.MODE === 'development') {
      FirebaseRouter.logRouting(collectionName, 'getConnection');
    }
    
    return connection;
  }
  
  /**
   * Get Firestore database for a collection
   * @param {string} collectionName - Name of the collection
   * @returns {Firestore} Firestore database instance
   */
  getDb(collectionName) {
    const connection = this.getConnection(collectionName);
    const db = connection.db;
    
    // 🔍 DEBUG: Log database and auth info
    if (import.meta.env.MODE === 'development') {
      const connectionType = FirebaseRouter.getConnectionType(collectionName);
      const auth = connection.auth;
      const user = auth?.currentUser;
      
      // console.log('🔍 [MultiFirebase DEBUG] getDb() called:', {
      //   collection: collectionName,
      //   target: connectionType,
      //   projectId: db.app.options.projectId,
      //   authDomain: db.app.options.authDomain,
      //   userAuthenticated: !!user,
      //   userUid: user?.uid,
      //   userEmail: user?.email
      // });
    }
    
    return db;
  }
  
  /**
   * Get Storage for a collection  
   * @param {string} collectionName - Name of the collection
   * @returns {FirebaseStorage} Storage instance
   */
  getStorage(collectionName) {
    return this.getConnection(collectionName).storage;
  }
  
  /**
   * Get connection info for debugging
   * @param {string} collectionName - Name of the collection
   * @returns {Object} Connection info
   */
  getConnectionInfo(collectionName) {
    const connection = this.getConnection(collectionName);
    const connectionType = FirebaseRouter.getConnectionType(collectionName);
    
    return {
      type: connectionType,
      name: connection.name,
      projectId: connection.app?.options?.projectId || 'Primary (SGC)',
      collection: collectionName
    };
  }
  
  /**
   * Test connectivity to all Firebase projects
   * @returns {Promise<Object>} Test results
   */
  async testConnectivity() {
    console.log('[MultiFirebaseService] Starting testConnectivity...');
    
    // Ensure initialization
    if (!this.initialized) {
      console.log('[MultiFirebaseService] Not initialized, initializing now...');
      this.initialize();
    }
    
    console.log('[MultiFirebaseService] Available connections:', Object.keys(this.connections));
    console.log('[MultiFirebaseService] Connections object:', this.connections);
    
    const results = {};
    
    // Check if connections exist
    if (!this.connections || Object.keys(this.connections).length === 0) {
      console.error('[MultiFirebaseService] No connections available!');
      return {
        primary: {
          status: 'error',
          error: 'Primary connection not initialized',
          name: 'Primary Firebase'
        },
        secondary: {
          status: 'error', 
          error: 'Secondary connection not initialized',
          name: 'Secondary Firebase'
        }
      };
    }
    
    for (const [type, connection] of Object.entries(this.connections)) {
      console.log(`[MultiFirebaseService] Testing ${type} connection:`, connection);
      
      try {
        // Simple read test - try to access a collection using Firebase v9 syntax
        const testCollectionName = type === 'primary' ? 'users' : 'cbtkdeduca_cursos';
        console.log(`[MultiFirebaseService] Testing collection "${testCollectionName}" on ${type}...`);
        console.log(`[MultiFirebaseService] Connection details for ${type}:`, {
          hasApp: !!connection.app,
          appProjectId: connection.app?.options?.projectId,
          appAuthDomain: connection.app?.options?.authDomain,
          connectionName: connection.name
        });
        
        // Use Firebase v9 modular syntax - extra defensive approach
        let testQuery;
        try {
          const colRef = collection(connection.db, testCollectionName);
          const q = query(colRef, limit(1));
          testQuery = await getDocs(q);
          
          console.log(`[MultiFirebaseService] Query successful for ${type}, result:`, testQuery);
        } catch (queryError) {
          console.error(`[MultiFirebaseService] Query failed for ${type}:`, queryError);
          console.error(`[MultiFirebaseService] Error details:`, {
            code: queryError.code,
            message: queryError.message,
            customData: queryError.customData
          });
          
          // Provide more specific error information
          if (queryError.code === 'permission-denied') {
            console.error(`[MultiFirebaseService] Permission denied - check Firestore rules for project:`, 
              connection.app?.options?.projectId);
          } else if (queryError.code === 'not-found') {
            console.error(`[MultiFirebaseService] Project or collection not found:`, 
              connection.app?.options?.projectId);
          }
          
          throw queryError;
        }
        
        results[type] = {
          status: 'connected',
          projectId: connection.app?.options?.projectId || (type === 'primary' ? 'sg-cbtkd' : 'sg-cbtkd-educa'),
          name: connection.name,
          testCollection: testCollectionName,
          hasData: !testQuery.empty
        };
        
        console.log(`[MultiFirebaseService] ${type} test successful:`, results[type]);
        
      } catch (error) {
        console.error(`[MultiFirebaseService] ${type} test failed:`, error);
        
        results[type] = {
          status: 'error',
          projectId: connection.app?.options?.projectId || (type === 'primary' ? 'sg-cbtkd' : 'sg-cbtkd-educa'),
          name: connection.name,
          error: error.message
        };
      }
    }
    
    console.log('[MultiFirebaseService] Final test results:', results);
    return results;
  }
}

// Export singleton instance
const multiFirebaseService = new MultiFirebaseService();

// Make available in development for console testing
if (import.meta.env.MODE === 'development') {
  window.multiFirebaseService = multiFirebaseService;
}

export default multiFirebaseService;